import type { ConversationTurnMessageDatagramBuf } from "~/classes/generated/message"
import type { ConversationTurnResponseDatagramBuf } from "~/classes/generated/response"

import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { ConversationTurnRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addConversationToBuf, parseConversation } from "~/utils/flatbuffers/conversation"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { parseConversationId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

import { onFileCreateResponse } from "../file/create"
import { onFileEditResponse } from "../file/edit"
import { onFileMoveResponse } from "../file/move"
import { onFileUpdateResponse } from "../file/update"

const console = useLogger("user/info", theme.colors.pink.hex)

export function sendConversationTurnRequest(
  request: ConversationTurnRequestDatagram,
) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const conversation = addConversationToBuf(builder, request.conversation)
  const requestId = addRequestIdToBuf(builder, request.requestId)

  ConversationTurnRequestDatagramBuf.startConversationTurnRequestDatagramBuf(builder)
  ConversationTurnRequestDatagramBuf.addRequestId(builder, requestId)
  ConversationTurnRequestDatagramBuf.addOffline(builder, request.offline)
  ConversationTurnRequestDatagramBuf.addConversation(builder, conversation)
  const convoRequest = ConversationTurnRequestDatagramBuf.endConversationTurnRequestDatagramBuf(builder)

  RequestDatagramBuf.startRequestDatagramBuf(builder)
  RequestDatagramBuf.addConversationTurnRequest(builder, convoRequest)
  const requests = DatagramBuf.createRequestVector(builder, [RequestDatagramBuf.endRequestDatagramBuf(builder)])
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requests)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function handleConversationTurnResponseDatagramBuf(
  datagram: ConversationTurnResponseDatagramBuf,
) {
  onConversationTurnResponseDatagram({
    requestId: parseRequestId(datagram.requestId()!),
    offline: datagram.offline(),
    error: maybe(parseDatagramError)(datagram.error()),
    conversationId: parseConversationId(datagram.conversationId()!),
    conversation: maybe(parseConversation)(datagram.conversation()),
    last: datagram.last(),
  })
}

export function onConversationTurnResponseDatagram(
  datagram: ConversationTurnResponseDatagram,
) {
  const { notifyError } = useAlerts()
  const { emitEntityEvent } = useEvents()

  if (isDefined(datagram.error) || !isDefined(datagram.conversation)) {
    notifyError("Conversation failure", "Failed to process conversation message")
    return console.error("Conversations info request failed", datagram)
  }
  emitEntityEvent("conversationProperties", datagram.conversation.id, datagram.conversation, datagram.offline)

  // make updates based on the turn itself
  const iConversation = new StaticConversation(datagram.conversation)
  for (const turn of iConversation.turns())
    emitEventsForConversationTurn(turn)
}

export function handleConversationTurnMessageDatagramBuf(
  datagram: ConversationTurnMessageDatagramBuf,
) {
  onConversationTurnMessageDatagram({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    conversation: parseConversation(datagram.conversation()!),
  })
}

export function onConversationTurnMessageDatagram(
  datagram: ConversationTurnMessageDatagram,
) {
  const { emitEntityEvent } = useEvents()
  emitEntityEvent("conversationProperties", datagram.conversation.id, datagram.conversation, false)

  const iConversation = new StaticConversation(datagram.conversation)
  for (const turn of iConversation.turns())
    emitEventsForConversationTurn(turn)
}

export function emitEventsForConversationTurn(
  turn: ConversationTurn,
) {
  const { emitEntityEvent } = useEvents()

  if ("user" in turn && typeof turn.user !== "string" && "input" in turn.user) {
    for (const _fragment of turn.user.input.fragments) {
      // TODO: any tasks for user fragments?
    }
  }
  else if ("agent" in turn && "status" in turn.agent) {
    for (const part of turn.agent.status.parts) {
      if ("fileLoadRequested" in part) {
        const request = part.fileLoadRequested.request
        const iFile = new StaticFile(request.file)
        emitEntityEvent("fileProperties", iFile.id, request.file, false)
        if (iFile.inferredParentId())
          emitEntityEvent("childProperties", iFile.inferredParentId()!, request.file, false)
      }
      else if ("fileLoadResponded" in part) {
        const response = part.fileLoadResponded.response
        if (response.file) {
          emitEntityEvent("fileProperties", response.file.id, response.file, false)
          emitEntityEvent("childProperties", response.file.id, response.file, false)
        }
        else
          console.error("File load errored", part)
      }
      else if ("fileCreateRequested" in part) {
        const request = part.fileCreateRequested.request
        const iFile = new StaticFile(request.file)
        emitEntityEvent("fileCreated", iFile.id, request.file, false)
        if (iFile.inferredParentId())
          emitEntityEvent("childCreated", iFile.inferredParentId()!, request.file, false)
      }
      else if ("fileCreateResponded" in part) {
        const response = part.fileCreateResponded.response
        onFileCreateResponse(response, true)
      }
      else if ("fileMoveRequested" in part) {
        const request = part.fileMoveRequested.request
        const iFile = new StaticFile(request.file)
        emitEntityEvent("fileProperties", iFile.id, request.file, false)
        if (iFile.inferredParentId())
          emitEntityEvent("childProperties", iFile.inferredParentId()!, request.file, false)
      }
      else if ("fileMoveResponded" in part) {
        const response = part.fileMoveResponded.response
        onFileMoveResponse(response, true)
      }
      else if ("fileUpdateRequested" in part) {
        // Handle file update requested, if needed
        const request = part.fileUpdateRequested.request
        const iFile = new StaticFile(request.file)
        emitEntityEvent("fileProperties", iFile.id, request.file, false)
        if (iFile.inferredParentId())
          emitEntityEvent("childProperties", iFile.inferredParentId()!, request.file, false)
      }
      else if ("fileUpdateResponded" in part) {
        const response = part.fileUpdateResponded.response
        onFileUpdateResponse(response, true)
      }
      else if ("fileEditRequested" in part) {
        const request = part.fileEditRequested.request
        const iFile = new StaticFile(request.file)
        emitEntityEvent("fileProperties", iFile.id, request.file, false)
        if (iFile.inferredParentId())
          emitEntityEvent("childProperties", iFile.inferredParentId()!, request.file, false)
      }
      else if ("fileEditResponded" in part) {
        const response = part.fileEditResponded.response
        onFileEditResponse(response, true)
      }
    }
  }
}
